import React, { useRef, useEffect, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import LiveScreen from '../LiveScreen';
import Chips from '../Chips';

/**
 * 04 · SPEAK — logging by sentence, with full context.
 * The typewriter cycles the exact utterances visible on the screen beside it —
 * the page speaks the same lines the product answers.
 */
const utterances = [
  'Drank a glass of water',
  'Log 75mg caffeine, had a cup of black coffee',
  'I ate 40g oats and 1 scoop of whey protein',
  '2 rotis and dal',
];

const Typewriter = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  // Ticks a state update every ~40ms — pause entirely while off-screen
  const isOnScreen = useInView(ref, { margin: '100px' });
  const [text, setText] = useState(reduce ? `"${utterances[0]}"` : '');

  useEffect(() => {
    if (reduce || !isOnScreen) return;
    let phrase = 0;
    let i = 0;
    let deleting = false;
    let timer;
    const tick = () => {
      const full = utterances[phrase];
      if (!deleting) {
        i += 1;
        setText(`"${full.slice(0, i)}"`);
        if (i === full.length) {
          deleting = true;
          timer = setTimeout(tick, 1700);
          return;
        }
        timer = setTimeout(tick, 42 + Math.random() * 40);
      } else {
        i -= 4;
        if (i <= 0) {
          i = 0;
          deleting = false;
          phrase = (phrase + 1) % utterances.length;
          setText('"');
          timer = setTimeout(tick, 500);
          return;
        }
        setText(`"${full.slice(0, i)}"`);
        timer = setTimeout(tick, 18);
      }
    };
    timer = setTimeout(tick, 800);
    return () => clearTimeout(timer);
  }, [reduce, isOnScreen]);

  return (
    <div ref={ref} className="flex items-center gap-3 rounded-2xl border border-ston-purple/25 bg-ston-purple/[0.07] px-5 py-3.5 font-mono text-[14px] text-ston-text min-h-[52px]">
      <span className="text-ston-purple-light shrink-0">›</span>
      <span>
        {text}
        <span className="inline-block w-[2px] h-[1.05em] ml-0.5 align-middle bg-ston-purple-light animate-pulse" />
      </span>
    </div>
  );
};

const StonAI = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const rise = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="ston-ai"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="ston-ai-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[560px] h-[560px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.1), rgba(147,51,234,0.050) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">

          {/* Device — left this time */}
          <motion.div
            className="flex justify-center lg:justify-start order-2 lg:order-1"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <LiveScreen
              dir={1}
              src="/screens/ston-ai-logging.webp"
              alt="STON AI logging water, caffeine and a meal from plain sentences"
              width={300}
            />
          </motion.div>

          {/* Copy */}
          <div className="order-1 lg:order-2">
            <motion.span className="section-label inline-block" {...rise(0)}>
              04 · SPEAK
            </motion.span>
            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(30px, 4vw, 52px)', letterSpacing: '-0.025em' }}
              {...rise(0.1)}
            >
              <span className="block text-ston-text">Just say it.</span>
              <span className="block gradient-text">It's logged.</span>
            </motion.h2>
            <motion.p
              className="mt-6 text-[17px] text-ston-text-secondary leading-relaxed max-w-[52ch]"
              {...rise(0.2)}
            >
              STON AI has the whole picture — every meal, every session, every lab
              marker, your sleep and your recovery. Ask it anything about your own
              body. And logging stops being a form:
            </motion.p>

            <motion.div className="mt-6 max-w-[440px]" {...rise(0.3)}>
              <Typewriter />
            </motion.div>

            <Chips className="mt-7" items={['Full context', 'Log by sentence', 'Knows your labs', 'Undo anything']} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StonAI;
